/**
 * ThresholdExtractor.js
 * 
 * A utility for extracting threshold values from various sources
 * and formats when processing trading signals.
 */

/**
 * Extracts a numeric threshold value from various input formats
 * 
 * @param {*} thresholdInput - The threshold in various possible formats
 * @param {Object} options - Additional options
 * @returns {number|null} - The extracted numeric threshold value or null if invalid
 */
export const extractThreshold = (thresholdInput, options = {}) => {
  const { defaultValue, allowZero = false, debug = false } = options;
  
  if (debug) {
    console.log(`[ThresholdExtractor] Extracting threshold from:`, thresholdInput);
  }
  
  // Handle null/undefined case
  if (thresholdInput === null || thresholdInput === undefined) {
    console.log(`[ThresholdExtractor] Null or undefined threshold, using default:`, defaultValue);
    return defaultValue !== undefined ? defaultValue : null;
  }
  
  // Direct numeric value
  if (typeof thresholdInput === 'number') {
    // Check for zero which might be invalid in some contexts
    if (thresholdInput === 0 && !allowZero) {
      console.log(`[ThresholdExtractor] Zero threshold detected but not allowed`);
      return defaultValue !== undefined ? defaultValue : null;
    }
    return thresholdInput;
  }
  
  // String that can be parsed as number
  if (typeof thresholdInput === 'string') {
    // If it's a numeric string, convert it
    const parsedValue = parseFloat(thresholdInput);
    if (!isNaN(parsedValue)) {
      // Check for zero
      if (parsedValue === 0 && !allowZero) {
        console.log(`[ThresholdExtractor] Zero threshold detected but not allowed`);
        return defaultValue !== undefined ? defaultValue : null;
      }
      return parsedValue;
    }
    
    // Try extracting numbers from strings like "30%", "RSI > 70", etc.
    const numberPattern = /[-+]?\d*\.?\d+/g;
    const matches = thresholdInput.match(numberPattern);
    if (matches && matches.length > 0) {
      const extractedValue = parseFloat(matches[0]);
      console.log(`[ThresholdExtractor] Extracted ${extractedValue} from string "${thresholdInput}"`);
      return extractedValue;
    }
    
    console.log(`[ThresholdExtractor] Could not extract numeric value from string:`, thresholdInput);
    return defaultValue !== undefined ? defaultValue : null;
  }
  
  // Threshold specified as an object
  if (typeof thresholdInput === 'object') {
    // Check for value property
    if (thresholdInput.value !== undefined) {
      return extractThreshold(thresholdInput.value, options);
    }
    
    // Check for threshold property
    if (thresholdInput.threshold !== undefined) {
      return extractThreshold(thresholdInput.threshold, options);
    }
    
    // Check for level property (sometimes used in indicators)
    if (thresholdInput.level !== undefined) {
      return extractThreshold(thresholdInput.level, options);
    }
  }
  
  console.log(`[ThresholdExtractor] Unrecognized threshold format:`, thresholdInput);
  return defaultValue !== undefined ? defaultValue : null;
};

/**
 * Extract threshold and override value from a configuration object
 * 
 * @param {Object} config - Configuration object
 * @param {string} indicatorType - The type of indicator
 * @returns {Object} - Object with threshold and operator values
 */
export const extractThresholdFromConfig = (config, indicatorType) => {
  // Default values for common indicator types
  const defaultThresholds = {
    rsi: { upper: 70, lower: 30 },
    stoch: { upper: 80, lower: 20 },
    macd: { upper: 0, lower: 0 },  // MACD crossing zero
    bollingerBands: { upper: 1, lower: -1 } // Normalized values
  };
  
  if (!config) {
    console.log(`[ThresholdExtractor] No config provided`);
    return { threshold: null, operator: null };
  }
  
  let threshold = null;
  let operator = null;
  
  // Handle direct threshold specification
  if (config.threshold !== undefined) {
    threshold = extractThreshold(config.threshold);
  } 
  // Handle level property
  else if (config.level !== undefined) {
    threshold = extractThreshold(config.level);
  }
  // Handle value property 
  else if (config.value !== undefined) {
    threshold = extractThreshold(config.value);
  }
  // Look for indicator-specific overrides in config
  else if (indicatorType && config[indicatorType]) {
    if (typeof config[indicatorType] === 'number') {
      threshold = config[indicatorType];
    } else if (typeof config[indicatorType] === 'object') {
      threshold = extractThreshold(config[indicatorType]);
    }
  } 
  // Look for upper/lower thresholds
  else if (config.upper !== undefined) {
    threshold = extractThreshold(config.upper);
    operator = 'crossover_above';
  } else if (config.lower !== undefined) {
    threshold = extractThreshold(config.lower);
    operator = 'crossover_below';
  }
  // Use default thresholds based on indicator type if available
  else if (indicatorType && defaultThresholds[indicatorType]) {
    // Use appropriate default based on operator
    if (config.operator === '>' || config.operator === 'crossover_above') {
      threshold = defaultThresholds[indicatorType].upper;
    } else if (config.operator === '<' || config.operator === 'crossover_below') {
      threshold = defaultThresholds[indicatorType].lower;
    }
  }
  
  // Extract operator if not already determined
  if (operator === null && config.operator !== undefined) {
    operator = config.operator;
  }
  
  console.log(`[ThresholdExtractor] Extracted threshold: ${threshold}, operator: ${operator} for ${indicatorType}`);
  return { threshold, operator };
};

export default {
  extractThreshold,
  extractThresholdFromConfig
};
